
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
    Grid,
    Divider,
} from '@material-ui/core/';
import { withStyles } from '@material-ui/core/styles';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import CompanyReportForm from './CompanyReportForm';
import EnhancedTable from '../common/EnhancedTable/EnhancedTable';
import LineGraph from '../common/LineGraph/LineGraph';
import PrintButton from '../common/PrintButton/PrintButton';
import routeConstants from '../../Routes/routeConstants';
import trackingUtils from '../../utils/trackingUtils';
import GridCard from '../common/GridCard/GridCard';
import PageHeader from '../common/PageHeader/PageHeader';
import NoResultMessage from '../common/NoResultMessage/NoResultMessage';
import HeaderWIthTextInline from '../common/HeaderWithTextInline/HeaderWithTextInline';
import ReportDataHeader from '../common/ReportDataHeader/ReportDataHeader';
import ReportPage from '../common/ReportPage/ReportPage';
import OrderSystemButton from '../common/OrderSystemButton/OrderSystemButton';
import translationPropType from '../../proptypes/translationPropType';
import CompanyReportSettings from './CompanyReportSettings';
import { mapProductSummaryToLeadTable, mapImpressionTable } from './companyReportTableAdapter';

const styles = theme => ({
    root: {
        flexGrow: 1,
        padding: theme.spacing(2),
        maxWidth: '100%',
    },
    '@media print': {
        hidePrint: {
            display: 'none',
        },
    },
});

const CompanyReportWrapper = ({
    classes,
    width,
    translation,
}) => {
    useEffect(() => {
        trackingUtils.trackPageview(routeConstants.COMPANY_REPORT);
    }, []);

    useEffect(() => {
        document.title = translation.COMPANY_REPORT_PAGE_TITLE;
    }, [translation]);

    const {
        companyreport: {
            reportGraphData,
            formState,
            showGraphs,
            showCustomerContacts,
            showImpressions,
            showPerHid,
            searchActive,
            reportHeader = translation.CONTACT_AND_EXPO_STATS,
            resultPeriodFrom,
            resultPeriodTo,
            resultSearchType,
            productSummary,
            totalLeadCount,
            notGroupedProducts,
            totalDetailImpressionCount,
            totalListImpressionCount,
            totalImpressionCount,
            companyCount,
            companyTableData,
        },
        user: {
            user,
        },
    } = useSelector(state => state);

    const isAdvancedUser = user ? user.isAdvancedUser : null;

    const noResult = searchActive && !companyCount;

    const leadTableData = productSummary ? mapProductSummaryToLeadTable(
        productSummary,
        translation,
        totalLeadCount,
        notGroupedProducts,
    ) : null;

    const impresssionTableData = productSummary
        && totalDetailImpressionCount >= 0
        && totalListImpressionCount >= 0
        && totalImpressionCount >= 0
        && translation ? mapImpressionTable(
            productSummary,
            totalDetailImpressionCount,
            totalListImpressionCount,
            totalImpressionCount,
            translation,
        ) : null;

    const totalLeadCountString = totalLeadCount ? totalLeadCount.toLocaleString('sv-se') : null;

    return (
        <div className={classes.root}>
            <Grid container spacing={2}>
                <GridCard xs={12} lg={10} xl={6} className={classes.hidePrint} cardOverflowVisible>
                    <PageHeader header={translation.COMPANY_REPORT} />
                    <CompanyReportForm
                        searchActive={searchActive}
                        translation={translation}
                    />
                </GridCard>
                {!noResult && searchActive && (
                    <Grid item xs={12} className={classes.hidePrint}>
                        <Divider />
                    </Grid>
                )}
                {!noResult && searchActive && (
                    <CompanyReportSettings
                        className={classes.hidePrint}
                        translation={translation}
                    />
                )
                }
                {searchActive && (
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                )}
                {noResult && (
                    <Grid item xs={12}>
                        <NoResultMessage translation={translation} />
                    </Grid>
                )}
                {!noResult && searchActive && (
                    <Grid item xs={12} className={classes.hidePrint}>
                        <PrintButton translation={translation} />
                        {resultSearchType === 'ORDER_NUMBER' && (
                            <OrderSystemButton
                                orderId={formState.ORDER_NUMBER}
                                translation={translation}
                            />
                        )}
                    </Grid>
                )}
                {!noResult
                && searchActive
                && showCustomerContacts
                && totalLeadCountString
                && leadTableData
                && (
                    <ReportPage printHeader={reportHeader}>
                        <Grid item xs={12}>
                            <HeaderWIthTextInline
                                header={`${translation.PERIOD}: `}
                                text={`${resultPeriodFrom} - ${resultPeriodTo}`}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <ReportDataHeader
                                data={totalLeadCountString}
                                dataType={translation.CUSTOMER_CONTACTS}
                            />
                        </Grid>
                        {isWidthUp('sm', width) && reportGraphData && reportGraphData.lead && showGraphs && (
                            <Grid item xs={12}>
                                <LineGraph
                                    graphData={reportGraphData ? reportGraphData.lead : {}}
                                    translation={translation}
                                />
                            </Grid>
                        )}
                        {totalLeadCountString && leadTableData && (
                            <Grid item xs={12}>
                                <EnhancedTable
                                    rows={leadTableData.dataRows}
                                    cols={leadTableData.dataCols}
                                    translation={translation}
                                    hideToolbar
                                    hidePagination
                                />
                            </Grid>
                        )}
                    </ReportPage>
                )}
                {!noResult
                && searchActive
                && showImpressions
                && impresssionTableData
                && (
                    <ReportPage printHeader={reportHeader}>
                        <Grid item xs={12}>
                            <HeaderWIthTextInline
                                header={`${translation.PERIOD}: `}
                                text={`${resultPeriodFrom} - ${resultPeriodTo}`}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <ReportDataHeader
                                data={impresssionTableData.totalCountString}
                                dataType={translation.IMPRESSIONS}
                            />
                        </Grid>
                        {isWidthUp('sm', width) && reportGraphData && reportGraphData.impression && showGraphs && (
                            <Grid item xs={12}>
                                <LineGraph
                                    graphData={reportGraphData ? reportGraphData.impression : {}}
                                    translation={translation}
                                />
                            </Grid>
                        )}
                        {impresssionTableData && (
                            <Grid item xs={12}>
                                <EnhancedTable
                                    rows={impresssionTableData.dataRows}
                                    cols={impresssionTableData.dataCols}
                                    translation={translation}
                                    hideToolbar
                                    hidePagination
                                />
                            </Grid>
                        )}
                    </ReportPage>
                )}
                {!noResult
                && companyTableData
                && isAdvancedUser
                && showPerHid
                && (
                    <ReportPage>
                        {companyTableData && (
                            <Grid item xs={12}>
                                <EnhancedTable
                                    rows={companyTableData.dataRows}
                                    cols={companyTableData.dataCols}
                                    translation={translation}
                                    hideToolbar
                                    fitInPage={companyTableData.dataRows.length}
                                />
                            </Grid>
                        )}
                    </ReportPage>
                )}
            </Grid>
        </div>
    );
};

CompanyReportWrapper.propTypes = {
    classes: PropTypes.objectOf(PropTypes.any).isRequired,
    translation: translationPropType.isRequired,
    width: PropTypes.string.isRequired,
};

export default withWidth()(withStyles(styles, { withTheme: true })(CompanyReportWrapper));
